import React, { useState, useEffect, useRef } from 'react';
import { Moon, Sun, Download, Mail, Phone, Linkedin, MapPin, Code, Award, Briefcase, GraduationCap, ChevronRight, ExternalLink, TrendingUp, Users, Zap, Cloud, Server, Database, Github } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';

import ContactForm from '../components/ContactForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Skill Progress Bar Component
const SkillBar = ({ skill, level }) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => setProgress(level), 100);
    }
  }, [isVisible, level]);

  return (
    <div ref={ref} className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{skill}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">{level}%</span>
      </div>
      <Progress value={progress} className="h-2" />
    </div>
  );
};

const skillNavCategories = [];

const ProjectCard = ({ project }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="hover:shadow-2xl transition-all group">
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setExpanded(o => !o)}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-xl group-hover:text-blue-600">{project.title}</CardTitle>
            <CardDescription className="font-semibold text-blue-600">{project.company}</CardDescription>
          </div>
          <div className="flex items-center gap-2 ml-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4 border-t pt-4">
          <p className="text-slate-600 dark:text-slate-300 text-justify">{project.description}</p>

          {/* Challenges */}
          {project.challenges && (
            <div className="flex items-start gap-2 pt-2 border-t">
              <Server className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-500 tracking-wide mb-1">Challenges:</p>
                <p className="text-sm text-slate-600 dark:text-slate-300 text-justify">{project.challenges}</p>
              </div>
            </div>
          )}

          {/* Design Decisions */}
          {project.decisions && (
            <div className="flex items-start gap-2 pt-2 border-t">
              <TrendingUp className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-blue-500 tracking-wide mb-1">Design Decisions:</p>
                <p className="text-sm text-slate-600 dark:text-slate-300 text-justify">{project.decisions}</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {project.technologies.map((tech, idx) => (
              <Badge key={idx} variant="secondary">{tech}</Badge>
            ))}
          </div>
          <div className="flex items-start gap-2 pt-3 border-t">
            <Zap className="h-5 w-5 text-amber-400 shrink-0" />
            <div>
              <p className="text-sm font-bold text-amber-400 tracking-wide">Impact:</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">{project.impact}</p>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const ExperienceCard = ({ exp }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="hover:shadow-xl transition-all">
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setExpanded(o => !o)}
      >
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Badge className="w-fit">{exp.period}</Badge>
            <CardTitle className="text-xl">{exp.title}</CardTitle>
            <CardDescription className="text-base font-semibold text-blue-600">
              {exp.company} — {exp.location}
            </CardDescription>
          </div>
          <div className={`ml-4 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          <ul className="space-y-2 border-t pt-4">
            {exp.achievements.map((achievement, idx) => (
              <li key={idx} className="flex items-start">
                <ChevronRight className="h-4 w-4 text-blue-600 mr-2 mt-0.5 shrink-0" />
                <span className="text-slate-600 dark:text-slate-300 text-sm">{achievement}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
};

const SkillSidebar = ({ skillSet }) => {
  const categories = Object.keys(skillSet || {});
  return (
  <aside className="hidden lg:flex flex-col fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-r border-slate-200 dark:border-slate-800 z-40 overflow-y-auto">
    <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white/95 dark:bg-slate-900/95">
      <p className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400">Skill Set</p>
    </div>
    <nav className="px-3 py-3 space-y-4">
      {Object.entries(skillSet || {}).map(([category, { items }]) => (
        <div key={category}>
          <button
            onClick={() => {
              const el = document.getElementById(`skill-${category.replace(/[^a-zA-Z0-9]/g, '-')}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
            className="w-full text-left text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wide hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-1"
          >
            {category}
          </button>
          <ul className="space-y-0.5 pl-2">
            {items.map((item, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-slate-400 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  </aside>
  );
};

const EnhancedPortfolio = ({ role = 'lead', portfolioData }) => {
  const [theme, setTheme] = useState('dark');
  const [skillsOpen, setSkillsOpen] = useState(false);
  const skillsRef = useRef(null);

  useEffect(() => {
    setTheme('dark');
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (skillsRef.current && !skillsRef.current.contains(e.target)) {
        setSkillsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const [otpModal, setOtpModal] = useState({ open: false, step: 'email', email: '', otp: '', role: '', loading: false, error: '' });

  const handleDownloadResume = () => {
    const role = portfolioData?.profile?.resumeFile || 'lead-fullstack';
    setOtpModal({ open: true, step: 'email', email: '', otp: '', role, loading: false, error: '' });
  };

  const handleOtpSend = async () => {
    setOtpModal(p => ({ ...p, loading: true, error: '' }));
    try {
      const res = await fetch(`${API}/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpModal.email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send code');
      setOtpModal(p => ({ ...p, step: 'otp', loading: false }));
    } catch (e) {
      setOtpModal(p => ({ ...p, loading: false, error: e.message }));
    }
  };

  const handleOtpVerify = async () => {
    setOtpModal(p => ({ ...p, loading: true, error: '' }));
    try {
      const res = await fetch(`${API}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpModal.email, otp: otpModal.otp })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');

      // Download with token
      const dlRes = await fetch(`${API}/resume/download?role=${otpModal.role}&token=${data.download_token}`);
      if (!dlRes.ok) throw new Error('Download failed');
      const blob = await dlRes.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setOtpModal(p => ({ ...p, open: false, loading: false }));
    } catch (e) {
      setOtpModal(p => ({ ...p, loading: false, error: e.message }));
    }
  };

  const defaultExpertiseAreas = [
    { name: 'AWS & Cloud Architecture', level: 95, icon: Cloud },
    { name: 'Node.js & TypeScript', level: 95, icon: Code },
    { name: 'Java / Spring Boot / J2EE', level: 90, icon: Server },
    { name: 'Python & Serverless Backend', level: 85, icon: Code },
    { name: 'Microservices & Event-Driven Architecture', level: 95, icon: Server },
    { name: 'REST / SOAP / API Integration', level: 92, icon: Database },
    { name: 'Middleware & Integration (MuleSoft, Camel, NiFi, Glue, Talend)', level: 78, icon: Database },
    { name: 'System Design & Architecture', level: 95, icon: TrendingUp },
    { name: 'DevOps & Infrastructure as Code', level: 90, icon: Database },
    { name: 'React & Frontend', level: 85, icon: Code },
    { name: 'Agentic AI & LLM Integration', level: 88, icon: Zap },
    { name: 'Team Leadership & Mentorship', level: 92, icon: Users },
    { name: 'Cross-Functional Collaboration', level: 90, icon: Users },
  ];

  const iconMap = { Cloud, Code, Server, Database, TrendingUp, Zap, Users };
  const expertiseAreas = portfolioData?.expertiseAreas
    ? portfolioData.expertiseAreas.map(a => ({ ...a, icon: iconMap[a.icon] || Code }))
    : defaultExpertiseAreas;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <SkillSidebar skillSet={portfolioData?.skillSet || {}} />
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/70 dark:bg-slate-950/70 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 z-50 shadow-sm">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Code className="h-7 w-7 text-blue-600 dark:text-blue-400" />
                <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-white dark:border-slate-950"></div>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Meeraiah Y.</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {['Home', 'About', 'Projects', 'Experience', 'Education', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 hover:scale-105"
                >
                  {item}
                </button>
              ))}

            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full hover:scale-110 transition-transform">
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </Button>
              <Button onClick={handleDownloadResume} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105">
                <Download className="h-4 w-4 mr-2" />
                Resume
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <div className="lg:pl-64">
      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/10 dark:bg-blue-600/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400/10 dark:bg-purple-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center space-y-8">
            {/* Passport Photo */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-32 h-40 rounded-xl overflow-hidden border-4 border-white dark:border-slate-700 shadow-2xl ring-4 ring-blue-500/30">
                  <img
                    src="/profile.jpg"
                    alt="Meeraiah Yampalla"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 h-6 w-6 bg-green-500 rounded-full border-2 border-white dark:border-slate-950 shadow-md"></div>
              </div>
            </div>

            <Badge variant="outline" className="text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400 px-6 py-2 text-sm font-medium shadow-sm">
              <Briefcase className="h-4 w-4 mr-2 inline" />
              Senior Engineering Leader
            </Badge>
            
            <div className="space-y-4">
              <h1 className="text-6xl md:text-7xl font-bold text-slate-900 dark:text-white tracking-tight">
                {portfolioData.profile.name}
              </h1>
              <div className="h-1 w-32 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full"></div>
            </div>
            
            <p className="text-3xl md:text-4xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              {portfolioData.profile.title}
            </p>
            
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Transforming enterprise platforms at scale • Cloud-native expert • Agentic AI & LLM integration • Leading high-impact teams
            </p>

            {/* Animated Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto pt-8">
              <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all hover:scale-105">
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  <AnimatedCounter end={22} suffix="+" />
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 font-medium mt-2">Years Experience</div>
              </div>
              
              <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all hover:scale-105">
                <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
                  <AnimatedCounter end={5} suffix="+" />
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 font-medium mt-2">Fortune 500</div>
              </div>
              
              <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all hover:scale-105">
                <div className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  <AnimatedCounter end={5} suffix="M+" />
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 font-medium mt-2">Users Impacted</div>
              </div>
              
              <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all hover:scale-105">
                <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  99.9<span className="text-2xl">%</span>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 font-medium mt-2">Uptime</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 justify-center pt-8">
              <Button onClick={() => scrollToSection('contact')} size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg px-8">
                Let's Connect
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button onClick={() => scrollToSection('projects')} variant="outline" size="lg" className="border-2 hover:scale-105 px-8">
                View Projects
              </Button>
              <Button variant="outline" size="lg" asChild className="border-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 hover:scale-105 px-6">
                <a href={portfolioData.profile.linkedin} target="_blank" rel="noopener noreferrer">
                  <Linkedin className="mr-2 h-5 w-5" />
                  LinkedIn
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-2 border-slate-700 dark:border-slate-400 text-slate-700 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:scale-105 px-6">
                <a href={portfolioData.profile.github} target="_blank" rel="noopener noreferrer">
                  <Github className="mr-2 h-5 w-5" />
                  GitHub
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-16 px-6 bg-white/50 dark:bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12">Core Expertise</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {expertiseAreas.map((area, idx) => {
              const Icon = area.icon;
              return (
                <div key={idx} className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl p-6 border shadow-md hover:shadow-xl transition-all hover:scale-105">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg mr-4">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <SkillBar skill={area.name} level={area.level} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12">Impact & Leadership</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-2xl transition-all hover:scale-105 group">
              <CardHeader>
                <CardTitle className="flex items-center text-blue-600">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Users className="h-6 w-6" />
                  </div>
                  Leadership
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 dark:text-slate-300">Mentoring teams and driving engineering standards across Fortune 500</p>
                <div className="mt-4 flex items-center text-sm text-blue-600 font-semibold">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  10+ engineers mentored
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all hover:scale-105 group">
              <CardHeader>
                <CardTitle className="flex items-center text-purple-600">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Cloud className="h-6 w-6" />
                  </div>
                  Cloud Architecture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 dark:text-slate-300">AWS Solutions Architect designing scalable serverless systems</p>
                <div className="mt-4 flex items-center text-sm text-purple-600 font-semibold">
                  <Zap className="h-4 w-4 mr-1" />
                  60% faster deployments
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all hover:scale-105 group">
              <CardHeader>
                <CardTitle className="flex items-center text-green-600">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Code className="h-6 w-6" />
                  </div>
                  Full-Stack
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 dark:text-slate-300">React frontends to Java/Node.js backends and AWS infrastructure</p>
                <div className="mt-4 flex items-center text-sm text-green-600 font-semibold">
                  <Server className="h-4 w-4 mr-1" />
                  10M+ events/day
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all hover:scale-105 group">
              <CardHeader>
                <CardTitle className="flex items-center text-orange-600">
                  <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Zap className="h-6 w-6" />
                  </div>
                  Agentic AI
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 dark:text-slate-300">Building AI-powered solutions with MCP Servers, RAG, Langchain, Langgraph, OpenAI & Claude using Kiro and Claude Code</p>
                <div className="mt-4 flex items-center text-sm text-orange-600 font-semibold">
                  <Zap className="h-4 w-4 mr-1" />
                  GPT-4 / Claude / RAG / MCP
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Experience, Projects, Contact sections similar to original but enhanced... */}
      {/* For brevity, using original sections with ContactForm */}
      
      <section id="projects" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12">Transformative Projects</h2>
          <div className="space-y-4">
            {portfolioData.projects.map((project) => (
              <ProjectCard key={`${project.id}-${project.title}`} project={project} />
            ))}
          </div>
        </div>
      </section>

      <section id="experience" className="py-20 px-6 bg-gradient-to-br from-slate-50 to-purple-50 dark:from-slate-950 dark:to-slate-900">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12">Professional Journey</h2>
          <div className="space-y-4">
            {portfolioData.experience.map((exp) => (
              <ExperienceCard key={exp.id} exp={exp} />
            ))}
          </div>
        </div>
      </section>


      {/* Education & Certifications */}
      <section id="education" className="py-20 px-6 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12">Education & Certifications</h2>
          <div className="grid md:grid-cols-2 gap-8">

            {/* Education */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl">
                  <GraduationCap className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Education</h3>
              </div>
              <div className="space-y-4">
                {portfolioData.education.map((edu) => (
                  <Card key={edu.id} className="hover:shadow-lg transition-all border-l-4 border-l-purple-500">
                    <CardContent className="pt-5">
                      <p className="font-bold text-slate-800 dark:text-white">{edu.degree}</p>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mt-1">{edu.university}</p>
                      <Badge variant="outline" className="mt-2 text-xs">{edu.year}</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl">
                  <Award className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Certifications</h3>
              </div>
              <div className="space-y-4">
                {portfolioData.certifications.map((cert) => (
                  <Card key={cert.id} className="hover:shadow-lg transition-all border-l-4 border-l-green-500">
                    <CardContent className="pt-5">
                      <p className="font-bold text-slate-800 dark:text-white">{cert.name}</p>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium mt-1">{cert.issuer}</p>
                      <Badge variant="outline" className="mt-2 text-xs">{cert.year}</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      <section id="contact" className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-center mb-12">Let's Build Something Great</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Email</p>
                    <a href={`mailto:${portfolioData.profile.email}`} className="text-slate-900 dark:text-white hover:text-blue-600 font-medium">
                      {portfolioData.profile.email}
                    </a>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Phone</p>
                    <a href={`tel:${portfolioData.profile.phone}`} className="text-slate-900 dark:text-white hover:text-green-600 font-medium">
                      {portfolioData.profile.phone}
                    </a>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center space-x-3">
                  <Linkedin className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">LinkedIn</p>
                    <a href={portfolioData.profile.linkedin} target="_blank" rel="noopener noreferrer" className="text-slate-900 dark:text-white hover:text-blue-600 font-medium">
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center space-x-3">
                  <Github className="h-5 w-5 text-slate-700 dark:text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">GitHub</p>
                    <a href={portfolioData.profile.github} target="_blank" rel="noopener noreferrer" className="text-slate-900 dark:text-white hover:text-slate-700 dark:hover:text-slate-400 font-medium">
                      View GitHub Profile
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
            <ContactForm />
          </div>
        </div>
      </section>

      <footer className="bg-gradient-to-br from-slate-900 to-slate-950 text-white py-12 px-6">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="flex justify-center items-center space-x-6 mb-6">
            <a href={portfolioData.profile.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-all hover:scale-110" title="Connect on LinkedIn">
              <Linkedin className="h-6 w-6" />
            </a>
            <a href={portfolioData.profile.github} target="_blank" rel="noopener noreferrer" className="hover:text-slate-400 transition-all hover:scale-110" title="View GitHub Profile">
              <Github className="h-6 w-6" />
            </a>
            <a href={`mailto:${portfolioData.profile.email}`} className="hover:text-blue-400 transition-all hover:scale-110" title="Send Email">
              <Mail className="h-6 w-6" />
            </a>
            <Button onClick={handleDownloadResume} variant="ghost" size="sm" className="text-white hover:text-blue-400 hover:bg-white/10">
              <Download className="h-4 w-4 mr-2" />
              Resume
            </Button>
          </div>
          <p className="text-sm text-slate-400">© {new Date().getFullYear()} Meeraiah Yampalla</p>
        </div>
      </footer>
      </div> {/* end lg:pl-56 */}

      {/* OTP Modal */}
      {otpModal.open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center px-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 w-full max-w-md border border-slate-200 dark:border-slate-700">
            {otpModal.step === 'email' ? (
              <>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Verify to Download</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Enter your email to receive a one-time verification code.</p>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={otpModal.email}
                  onChange={e => setOtpModal(p => ({ ...p, email: e.target.value }))}
                  className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-4 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {otpModal.error && <p className="text-xs text-red-500 mb-3">{otpModal.error}</p>}
                <div className="flex gap-3">
                  <button onClick={() => setOtpModal(p => ({ ...p, open: false }))} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">Cancel</button>
                  <button onClick={handleOtpSend} disabled={otpModal.loading || !otpModal.email} className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50">
                    {otpModal.loading ? 'Sending…' : 'Send Code'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Enter Verification Code</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">A 6-digit code was sent to <span className="font-medium text-blue-600">{otpModal.email}</span>. It expires in 10 minutes.</p>
                <input
                  type="text"
                  placeholder="000000"
                  maxLength={6}
                  value={otpModal.otp}
                  onChange={e => setOtpModal(p => ({ ...p, otp: e.target.value.replace(/\D/g, '') }))}
                  className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-4 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white mb-3 text-center tracking-widest text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {otpModal.error && <p className="text-xs text-red-500 mb-3">{otpModal.error}</p>}
                <div className="flex gap-3">
                  <button onClick={() => setOtpModal(p => ({ ...p, step: 'email', otp: '', error: '' }))} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">Back</button>
                  <button onClick={handleOtpVerify} disabled={otpModal.loading || otpModal.otp.length !== 6} className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50">
                    {otpModal.loading ? 'Verifying…' : 'Verify & Download'}
                  </button>
                </div>
                <button onClick={handleOtpSend} className="w-full mt-3 text-xs text-slate-400 hover:text-blue-500 text-center">Resend code</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedPortfolio;
